import type {
	AppSpecData,
	BuilderCompData,
	BuilderComponentsData,
	BuilderPublicPackagesUrls,
	ViewerClientSpecMap,
} from '@wix/thunderbolt-symbols'

const LOCAL_PILER_PREFIX = 'http://localhost:5555'
const PROD_PILER_PREFIX = 'https://siteassets.parastorage.com'

export type IGetBuilderComponentsData = (
	clientSpecMap: ViewerClientSpecMap,
	serviceTopology?: Record<string, string>,
	useLocalPiler?: boolean
) => BuilderComponentsData

const builderPublicPackages = ['builder-state', 'builder-velo', 'builder-wix-components']

export const getPublicPackagesUrls = (serviceTopology: Record<string, string>): BuilderPublicPackagesUrls => {
	const builderBundleUrlFor = (pkg: string, format: string) => {
		const suffix = format === 'es' ? 'mjs' : 'js'
		return `${serviceTopology[pkg]}/${format}/${pkg}.${suffix}`
	}

	const urls = {
		umd: {},
		esm: { 'services-manager-react': `${serviceTopology['services-manager-react']}/index.js` },
	} as BuilderPublicPackagesUrls

	return builderPublicPackages.reduce((acc, pkg) => {
		acc.umd[pkg] = builderBundleUrlFor(pkg, 'umd')
		acc.esm[pkg] = builderBundleUrlFor(pkg, 'es')
		return acc
	}, urls)
}

export const getBuilderComponentsData: IGetBuilderComponentsData = (
	clientSpecMap,
	serviceTopology,
	useLocalPiler
): BuilderComponentsData => {
	const componentsData = Object.values(clientSpecMap).reduce((acc, spec: AppSpecData) => {
		const { appDefinitionId, components } = spec
		if (!components) {
			return acc
		}

		components
			.filter((comp) => comp.type === 'EDITOR_REACT_COMPONENT')
			.forEach((comp) => {
				const { componentId, data } = comp
				const { type, resources } = data
				const sdkUrl = resources.sdk?.url
				let serverUrl = resources.server?.url || ''
				let componentUrl = resources.client.componentUrl || ''
				if (useLocalPiler) {
					serverUrl = serverUrl?.replace(PROD_PILER_PREFIX, LOCAL_PILER_PREFIX)
					componentUrl = componentUrl?.replace(PROD_PILER_PREFIX, LOCAL_PILER_PREFIX)
				}

				acc[type] = {
					appDefinitionId,
					componentId,
					componentUrl,
					cssUrl: resources?.client?.cssUrl || '',
					viewerReducedModel: resources?.viewerReducedModel || '',
					...(sdkUrl && {
						sdkUrl,
					}),
					...(serverUrl && {
						serverUrl,
					}),
				}
			})

		return acc
	}, {} as Record<string, BuilderCompData>)

	return {
		components: componentsData,
		publicPackagesUrls: serviceTopology ? getPublicPackagesUrls(serviceTopology) : {},
	} as BuilderComponentsData
}
